// src/features/leases/pages/components/LeasesListTable.jsx
//
// LEASES-LIST-REDESIGN — compact "List" view for the Leases landing page.
// Office · Hub · Outlook · Status · Expire Date · Arrangement · Term Type ·
// Sq Ft · Annual Rent · $/SF. Arrangement labels are unchanged.
//
// LEASES-LIST-TIGHTEN:
//   - "2026 Rent" column removed; Region column kept.
//   - Risk Score chip shows whole number (Math.round).
//   - Status pills use the five canonical occupancy buckets.
//   - Type column: short labels via LEASE_DISPLAY_SHORT local map.
//   - Next Critical Date: inline "date · type" on one line.
//
// LEASES-LIST-RENEWAL-SQFT:
//   - Added Renewal Option column (130 px) after Expire Date.
//   - Added Sq Ft column (90 px) after $/sf.
//
// LEASES-LIST-COLUMN-REORDER-DROP-RENEWAL:
//   - Removed Renewal Option column (low-value in list view; still on detail page).
//   - Reordered to identity→timing→economics:
//     Location · Status · Type · Sq Ft · Expire Date · Risk Score · Annual Rent · $/SF
//   - Total 8-column width: 224+120+140+90+110+64+110+80 = 938 px
//
// LEASES-LIST-COLUMNS-FINAL:
//   - "Location" heading renamed to "Office"; "Risk Score" heading renamed to "Risk".
//   - Risk column moved to position 2 (mirrors Health in the Offices list).
//   - Final order: Office · Risk · Status · Type · Sq Ft · Expire Date · Annual Rent · $/SF
//   - Office pinned at 225 px (= LIST_COL_LOCATION_W); Risk at 72 px (= LIST_COL_INDICATOR_W).
//   - 6 trailing columns share LIST_COL_LEASES_TRAILING_W (100 px each); minWidth = 897 px.
//   - <colgroup> added to hard-pin col 1 (Office, 225 px) and col 2 (Risk, 72 px) so
//     viewport growth does not widen them beyond their canonical sizes.
//
// Props:
//   leases           — pre-filtered, pre-sorted array
//   cdByLease        — { [leaseId]: CriticalDateRow[] } for Next CD column (optional)
//   onSelectLocation — (locationId: string, tab?: string, leaseId?: string) => void

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { activateCollectionRow } from "../../../../utils/collectionRowNavigation.js";
import { useNavigate } from "react-router-dom";
import { log } from "../../../../utils/logger.js";
import { T } from "../../../../app/tokens.js";
import { AnnualRentBreakdown } from "../../components/AnnualRentBreakdown.jsx";
import { getColHeaderHighlight } from "../../../../v2/utils/sortColumnHighlight.js";
import { CollectionSortHeader, CollectionSortCaret } from "../../../../v2/components/CollectionSortHeader.jsx";
import { S } from "../../../../app/styles.js";
import { LEASE_DISPLAY } from "../../../../utils/leaseTypeConstants.js";
import { resolveTimelineEndAnchor, parseDateOnly, ACTIVE_LABEL, URGENT_LABEL } from "../../leaseTabConfig.js";
import { ColorLegendPopup, dotIndicator } from "../../../../components/ColorLegendPopup.jsx";
import { HeadingTooltip } from "../../../../components/HeadingTooltip.jsx";
import { useHoverIntent } from "../../../../components/useHoverIntent.js";
import {
  LIST_COL_LEASES_LOCATION_W,
  LIST_COL_LEASES_HEALTH_W,
  LIST_COL_LEASES_STATUS_W,
  LIST_COL_LEASES_TRAILING_W,
  LIST_COL_LEASES_HUB_W,
  LIST_COL_LEASES_ANNUAL_RENT_W,
  LIST_COL_LEASES_EXPIRE_DATE_W,
} from "../../../../utils/listColumnWidths.js";
import { fmtMoney, fmtSqft } from "../../../../utils/format.js";
import { currentLeaseFootprint, displayedLeasePsf } from "../../../../../shared/leaseAreaBasis.js";
import { buildLeaseUrl } from "../../../../services/buildUrl.js";
import { matchLeaseIdentity } from "../../leaseIdentitySearch.js";
import { rankIdentityMatches } from "../../../../utils/curatedSearch.js";
import { IdentityMatchHint } from "../../../../components/IdentityMatchHint.jsx";
import { LEASES_EMPTY_STATE_MESSAGE, LEASES_TRULY_EMPTY_MESSAGE, statusFor } from "./LeasePortfolioFilters.jsx";
import { getCountdownStyle, computeLeaseRisk } from "../../leaseRisk.js";
import { classifyLeaseHealth, OUTLOOK_DESCRIPTOR_ORDER } from "../../deriveLeaseActions.js";
import { OverflowTable } from "../../../../v2/components/ListOverflow.jsx";
import { LEASE_STATUS_LABELS as STATUS_LABELS, LEASE_STATUS_META as _STATUS_META } from "../../leaseStatusLabels.js";
import { LEASE_STATUS_SORT_ORDER } from "../../leasePortfolioSort.js";
import { formatLongExpiryDate, isPastExpiryDate } from "../../expiryDisplay.js";
import { useLeasesListGeometry } from "./useLeasesListGeometry.js";
import { LIST_HEADER_RADIUS, listHeaderEdges, COLLECTION_GEOMETRY, collectionRowHeight } from "../../../../v2/components/listPresentation.js";
import { useListHeaderStuck } from "../../../../v2/hooks/useListHeaderStuck.js";
import { useCollectionCellTitles } from "../../../../v2/hooks/useCollectionCellTitles.js";

// Keyboard-accessible sort headers need a :focus-visible ring that inline
// styles cannot express. Mirrors the GANTT_TH_CSS pattern in PortfolioGantt.jsx.
//
// Responsive hiding: $/SF and Annual Rent hide first (narrowest viewports),
// then Type. Location / Risk Score / Status / Expire Date are always visible.
// Breakpoints chosen from the sum of column widths at each hide step.
const LEASES_TH_CSS = `
.leases-sort-th:focus-visible {
  outline: 2px solid ${T.blue};
  outline-offset: -2px;
}
@media (max-width: 975px) {
  .leases-list-legacy .leases-col-rent, .leases-list-legacy .leases-col-psf, .leases-list-legacy .leases-col-sqft { display: none; }
}
@media (max-width: 785px) {
  .leases-list-legacy .leases-col-type, .leases-list-legacy .leases-col-term { display: none; }
}
.leases-list-v2 td { padding: 0 4px !important; }
.leases-list-v2 td.leases-col-sqft,
.leases-list-v2 td.leases-col-rent,
.leases-list-v2 td.leases-col-psf { padding: 0 5px !important; }
.leases-list-v2 td:first-child { padding-left: 14px !important; }
.leases-list-v2 td.leases-col-psf { padding-right: 14px !important; }
.leases-list-v2 [data-row-name] {
  max-width: calc(100% + 11px) !important;
  padding: 3px 4px 3px 7px !important;
  margin: -3px -4px -3px -7px !important;
}
.leases-list-v2 td.leases-spare { padding: 0 !important; }
.leases-collection-row:not(.collection-keyboard-row):focus-visible {
  outline: 2px solid ${T.blue};
  outline-offset: -2px;
}
.leases-list-v2 .leases-sort-th {
  padding: 0 4px !important;
  height: ${COLLECTION_GEOMETRY.header}px;
  box-sizing: border-box;
  letter-spacing: normal !important;
  font-size: ${T.fsXxxs5}px !important;
}
/* Symmetric header insets keep each label centred on its data track. */
.leases-list-v2 .leases-sort-th:first-child,
.leases-list-v2 .leases-sort-th.leases-col-psf { padding: 0 14px !important; }
.leases-list-v2 thead tr { height: ${COLLECTION_GEOMETRY.header}px; }
.leases-list-v2 td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.leases-list-v2[data-density="compact"] [data-testid="status-pill"],
.leases-list-v2[data-density="compact"] [data-testid="health-pill"] {
  height: 20px !important; font-size: ${T.fsXxs}px !important;
}
.leases-list-v2 .leases-sort-th svg { width: 9px; height: 9px; margin-left: 1px !important; }
.leases-list-v2 .leases-sort-th svg { left: calc(100% + 1px) !important; margin-left: 0 !important; }
.leases-list-v2 .leases-col-type, .leases-list-v2 .leases-col-term {
  overflow: hidden;
  text-overflow: ellipsis;
}
.leases-list-v2 [data-testid="lease-type-value"],
.leases-list-v2 [data-testid^="sublease-head-"] {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.leases-list-v2 [data-testid="lease-type-value"] { flex: 0 1 auto; }
.leases-list-v2 [data-testid^="sublease-head-"] { flex: 0 1 auto; min-width: 0; }
.leases-list-v2 [data-testid="annual-rent-cell"] {
  overflow: hidden;
  text-overflow: ellipsis;
}
.leases-list-v2 [data-testid="expire-date-value"] { min-width: 0 !important; flex-shrink: 0; }
.leases-list-v2 [data-testid="expire-countdown"] { min-width: 0 !important; overflow: hidden; text-overflow: ellipsis; }
@container leases-list (max-width: 900px) {
  .leases-list-v2 .leases-col-rent, .leases-list-v2 .leases-col-psf, .leases-list-v2 .leases-col-sqft { display: none; }
  .leases-list-v2 col.leases-col-rent, .leases-list-v2 col.leases-col-psf, .leases-list-v2 col.leases-col-sqft { width: 0 !important; }
  .leases-list-v2 td.leases-col-term { padding-right: 14px !important; }
  .leases-list-v2 .leases-sort-th.leases-col-term { padding: 0 14px !important; }
}
@container leases-list (max-width: 650px) {
  .leases-list-v2 .leases-col-type, .leases-list-v2 .leases-col-term { display: none; }
  .leases-list-v2 col.leases-col-type, .leases-list-v2 col.leases-col-term { width: 0 !important; }
  .leases-list-v2 td:nth-child(5) { padding-right: 14px !important; }
  .leases-list-v2 .leases-sort-th:nth-child(5) { padding: 0 14px !important; }
}
`;

// ── Column definitions ─────────────────────────────────────────────────────────

// Shared border-radius for the list card and its inner header row.
// Both must use this constant — the card has no overflow:hidden, so without the
// inner-row radius the header background escapes the card's rounded top corners.
const PANEL_RADIUS = LIST_HEADER_RADIUS;

// 9-column width budget: Office(225) + Hub(64) + Outlook(120) + Status(100) + Expire Date(210)
//   + Type(100) + Sq Ft(100) + Annual Rent(150) + $/SF(100) = 1169 px min
// Order: Office · Hub · Outlook · Status · Expire Date · Type · Sq Ft · Annual Rent · $/Sq Ft
// LEASES-LIST-RISK-TO-RAG-HEALTH: "Risk" column replaced by "Health" RAG pill.
//   Width bumped 72→120 so "Winding down" never wraps; column label → "OUTLOOK".
// LEASES-SORT-COLUMNS-S1: end_date defaults to asc-first (soonest expiring first).
// LEASES-LIST-TABLE-POLISH: Expire Date widened 190→210 px.
// LEASES-LIST-HEALTH-POLISH: risk_score defaultDir changed to 1 (ASC = urgent-first
//   via HEALTH_SEVERITY_ORDER); Expire Date moved to immediately after Status.
const COLUMNS = [
  { key: "location",   label: "Office",      sortable: true,  width: LIST_COL_LEASES_LOCATION_W                                                      },
  { key: "hub",        label: "Hub",         sortable: true,  width: LIST_COL_LEASES_HUB_W,         align: "center", defaultDir: -1                   },
  { key: "risk_score", label: "Outlook",     sortable: true,  width: LIST_COL_LEASES_HEALTH_W,      defaultDir: 1,  align: "center"                   },
  { key: "status",     label: "Status",      sortable: true,  width: LIST_COL_LEASES_STATUS_W,      align: "center"                                    },
  { key: "end_date",   label: "Expire Date", sortable: true,  width: LIST_COL_LEASES_EXPIRE_DATE_W, defaultDir: 1                                      },
  { key: "type",       label: "Arrangement", sortable: true,  width: LIST_COL_LEASES_TRAILING_W,    colClass: "leases-col-type"                        },
  { key: "term_type",  label: "Term Type",   sortable: true,  width: LIST_COL_LEASES_TRAILING_W,    colClass: "leases-col-term"                        },
  { key: "sqft",       label: "Sq Ft",       sortable: true,  width: LIST_COL_LEASES_TRAILING_W,    defaultDir: -1, align: "right", colClass: "leases-col-sqft" },
  { key: "total_cost", label: "Annual Rent", sortable: true,  width: LIST_COL_LEASES_ANNUAL_RENT_W, defaultDir: -1, align: "right", colClass: "leases-col-rent" },
  { key: "psf",        label: "$/SF",        sortable: true,  width: LIST_COL_LEASES_TRAILING_W,    defaultDir: -1, colClass: "leases-col-psf"         },
];

const HEADER_EXPLANATIONS = {
  hub: "Hub offices are our anchor locations: population centers and centers of gravity with full-service on-site IT support and other services. The diamond marks a hub; not every office is one.",
  risk_score: "How soon this lease needs attention, based on the next decision point or expiry. Secure = more than 2 years out; Model = 1-2 years; Negotiate = within a year; Decide = within 90 days. Other states show posture rather than timing: Holdover (past expiry, still occupied), Sublease or Wind-Down (being exited), Draft, or Expired.",
  status: "Occupancy: Draft, Committed, In-Use, Vacant-Held, or Expired. Arrangement and sourced Term Type are independent of occupancy and Outlook.",
  term_type: "Source-provided lease behavior, independent of occupancy. Blank means no supplied value.",
  type: "The lease structure: Direct (leased directly from the landlord), Sublease (space taken from another tenant rather than the landlord), or Serviced office (turnkey space where facilities, furniture, and services are bundled into a single fee - e.g. a WeWork-style provider).",
  total_cost: "Recorded annual base rent for this lease, converted to USD. Past-expiry leases show the last recorded amount applicable through expiry. Rent only - it does not include operating expenses (OpEx), taxes, or fit-out costs.",
  psf: "List annual base rent in USD divided by the displayed SQ FT: available current lease-area footage, otherwise unit-normalized stored lease size. Unavailable when footage is unknown/nonpositive or rent is missing. This is not a historical or period-matched Financials rate.",
};

const HEADER_EXPLANATION_STYLE = {
  background: T.tooltip.bg,
  color: T.tooltip.fg,
  boxShadow: T.tooltip.shadow,
  fontSize: T.tooltip.fontSize,
  fontWeight: T.tooltip.fontWeight,
  lineHeight: T.tooltip.lineHeight,
  padding: "9px 13px",
  borderRadius: 6,
  whiteSpace: "normal",
  width: 300,
  maxWidth: "calc(100vw - 16px)",
  zIndex: 9999,
  pointerEvents: "none",
  textAlign: "left",
  textTransform: "none",
  letterSpacing: 0,
};

// ── Health pill style map ──────────────────────────────────────────────────────
// LEASES-LIST-HEALTH-POLISH: keyed by colorTier ("red"|"amber"|"green"|"slate"|"grey").
// Derives color + background from the same _STATUS_META token source used by
// StatusCell so both pills are visually consistent (same font, shape, border).
// Red has no matching status entry; it uses the RAG red tokens directly.
const HEALTH_TIER_STYLE = {
  green: { color: _STATUS_META.in_use.color,      background: _STATUS_META.in_use.bg      },
  amber: { color: T.ragAmber,                      background: T.ragAmberBg                 },
  slate: { color: _STATUS_META.vacant_held.color,  background: _STATUS_META.vacant_held.bg  },
  grey:  { color: _STATUS_META.expired.color,      background: _STATUS_META.expired.bg      },
  blue:  { color: _STATUS_META.draft.color,        background: _STATUS_META.draft.bg        },
  red:   { color: T.ragRed,                        background: T.ragRedBg                   },
};

// HEALTH_SEVERITY_ORDER removed (L-34): replaced by OUTLOOK_DESCRIPTOR_ORDER
// imported from deriveLeaseActions.js, which assigns each amber-tier bucket its
// own ordinal so amber leases never interleave when sorted by Outlook.

// ── Next critical date full labels ────────────────────────────────────────────

const CD_FULL_LABEL = {
  renewal_notice:       "Renewal notice",
  break_option:         "Break option",
  expansion_option:     "Expansion option",
  lease_expiry:         "Lease expiry",
  financial_obligation: "Financial obligation",
  regulatory:           "Regulatory",
};

function cdFullLabel(type) {
  return CD_FULL_LABEL[type] || (type || "").replace(/_/g, " ");
}

// ── Status pill ────────────────────────────────────────────────────────────────
// OFFICE-OVERVIEW-LEASE-STATUS-CONTROL-S1: STATUS_LABELS + style map are now
// defined in leaseStatusLabels.js. Imported at the top of this file (see top
// imports block). Re-export STATUS_LABELS for callers of this module.
export { STATUS_LABELS };

const STATUS_STYLE_MAP = Object.fromEntries(
  Object.entries(_STATUS_META).map(([k, v]) => [k, { color: v.color, background: v.bg }])
);

// Minimum pill width for Outlook (health) badges — measured as the max width across
// all 9 Outlook word labels at the pill's exact font spec (T.fwSemibold / T.fsXs /
// sans-serif), plus 2 × 10 px horizontal padding. Falls back to 90 px for SSR / headless.
const OUTLOOK_PILL_MIN_W = (() => {
  try {
    if (typeof document === "undefined") return 90;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 90;
    ctx.font = `${T.fwSemibold} ${T.fsXs}px sans-serif`;
    const labels = ["Secure", "Draft", "Sublease", "Expired", "Model", "Negotiate", "Wind-Down", "Decide", "Holdover"];
    return Math.max(...labels.map(l => Math.ceil(ctx.measureText(l).width))) + 20;
  } catch {
    return 90;
  }
})();

// Minimum pill width for Status badges — measured as the max width across all 6
// Status labels at the pill's exact font spec (T.fwSemibold / T.fsXs / sans-serif),
// plus 2 × 8 px horizontal padding. Falls back to 90 px for SSR / headless.
const STATUS_PILL_MIN_W = (() => {
  try {
    if (typeof document === "undefined") return 90;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 90;
    ctx.font = `${T.fwSemibold} ${T.fsXs}px sans-serif`;
    const labels = ["In-Use", "Committed", "Draft", "Vacant-Held", "Expired"];
    return Math.max(...labels.map(l => Math.ceil(ctx.measureText(l).width))) + 16;
  } catch {
    return 90;
  }
})();

function StatusCell({ lease }) {
  const bucket = statusFor(lease);
  const label  = STATUS_LABELS[bucket] || bucket;
  const tokens = STATUS_STYLE_MAP[bucket];
  if (!tokens) {
    log.warn("StatusCell: unknown status bucket", { bucket, leaseId: lease.id });
  }
  const { color, background } = tokens || { color: "var(--v2-mid)", background: "var(--v2-tint)" };

  return (
    <span
      data-testid="status-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 6,
        height: 22,
        lineHeight: 1,
        padding: "0 8px",
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        whiteSpace: "nowrap",
        textAlign: "center",
        minWidth: STATUS_PILL_MIN_W,
        color,
        background,
      }}
    >
      {label}
    </span>
  );
}

// ── Type display overrides (list-only, does NOT touch leaseTypeConstants.js) ──

const LEASE_DISPLAY_SHORT = {
  serviced_office:   "Serviced",
  sublease_outbound: "Sub (Out)",
  sublease_inbound:  "Sub (In)",
};

const LEASE_RELATIONSHIP_TEXT_STYLE = {
  display: "block",
  width: "max-content",
  maxWidth: "none",
  alignSelf: "center",
  color: "var(--v2-light)",
  fontSize: T.fsXxs,
  whiteSpace: "nowrap",
  textAlign: "center",
};

// ── Internal sort ─────────────────────────────────────────────────────────────

function listRentUsd(lease) {
  if (lease._listRentUsd !== undefined) return lease._listRentUsd;
  if (lease.listDisplayRentUsd !== undefined) return lease.listDisplayRentUsd;
  return lease.annualRentUsd ?? null;
}

function sortValue(lease, key) {
  switch (key) {
    case "term_type":   return lease.termType?.toLowerCase() ?? null;
    case "location":    return (lease.city || "").toLowerCase();
    case "type": {
      const raw = lease.leaseType || lease.type || "";
      return (LEASE_DISPLAY_SHORT[raw] || LEASE_DISPLAY[raw] || raw).toLowerCase();
    }
    case "risk_score": {
      // L-34: per-descriptor ordinal (primary) + expire-date ms (secondary).
      // OUTLOOK_DESCRIPTOR_ORDER gives each amber bucket its own rank so amber
      // leases group by descriptor rather than interleaving by date alone.
      // Unknown / null buckets fall to ordinal 9 (after all named buckets).
      const ordinal = OUTLOOK_DESCRIPTOR_ORDER[lease._healthBucket] ?? 9;
      const anchor = resolveTimelineEndAnchor(lease);
      const d = anchor.date ? parseDateOnly(anchor.date) : null;
      const ms = d ? d.getTime() : Number.MAX_SAFE_INTEGER;
      // Composite: ordinal (major) + date ms (minor). Scale factor 1e13 ensures
      // date differences (order of 1e12 ms) never overflow into the ordinal digit.
      return ordinal * 1e13 + ms;
    }
    // LEASES-LIST-EXPIRE-BLEED-v2 / HUB-SORTABLE: binary sort — hub rows (1)
    // group together at one end, non-hub rows (0) at the other, toggled by dir.
    case "hub":         return lease.isHub ? 1 : 0;
    // Annual Rent displays listDisplayRentUsd when the API provides it, with
    // annualRentUsd as a compatibility fallback for existing callers/fixtures.
    // Null rows land last — handled via early-exit in applySortInternal.
    case "total_cost":  return listRentUsd(lease);
    // $/sf uses the list's USD rent and size; display and sort both key on _psf.
    case "psf":         return lease._psf ?? Infinity;
    case "sqft":        return currentLeaseFootprint(lease).grossSf ?? Infinity;
    case "end_date": {
      // Sort purely by date value; rows with no date sort last regardless of status.
      const anchor = resolveTimelineEndAnchor(lease);
      if (!anchor.date) return Infinity;
      const d = parseDateOnly(anchor.date);
      return d ? d.getTime() : Infinity;
    }
    // LSE-LANDING-STATUS-SORT-ORDER: Status sort uses LEASE_STATUS_SORT_ORDER
    // (draft=0 → in_use=1 → vacant_held=2 → sublease_out=3 → expired=4) so that
    // ASC sorts Draft-first and DESC sorts Expired-first, matching the test
    // expectations and the smart-sort grouping convention.
    case "status": {
      const bucket = statusFor(lease);
      return LEASE_STATUS_SORT_ORDER[bucket] ?? 99;
    }
    default:            return "";
  }
}

// LIST-SORT-ROLLOUT-S1: sortKey2/sortDir2 are optional — they let the shared
// toolbar SortControl's "Then by" tie-break carry into the List view without
// touching the primary-sort behavior the tests above exercise (both default
// to null/1, a no-op, when omitted).
function applySortInternal(leases, sortKey, sortDir, sortKey2 = null, sortDir2 = 1) {
  if (!sortKey) return leases;
  return [...leases].sort((a, b) => {
    // total_cost: null list-rent rows land last in both ASC and DESC,
    // regardless of direction. Mirrors the m_and_a null-last pattern.
    if (sortKey === "total_cost") {
      const aRent = listRentUsd(a);
      const bRent = listRentUsd(b);
      const aNull = aRent == null;
      const bNull = bRent == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    // psf: null _psf rows land last in both directions, regardless of the
    // Infinity sentinel. Explicit guard prevents edge-case drift.
    if (sortKey === "psf") {
      const aNull = a._psf == null;
      const bNull = b._psf == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    // sqft: null/zero size rows land last in both directions.
    if (sortKey === "sqft") {
      const aNull = currentLeaseFootprint(a).grossSf == null;
      const bNull = currentLeaseFootprint(b).grossSf == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    const av = sortValue(a, sortKey);
    const bv = sortValue(b, sortKey);
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    if (sortKey2) {
      const av2 = sortValue(a, sortKey2);
      const bv2 = sortValue(b, sortKey2);
      if (av2 < bv2) return -sortDir2;
      if (av2 > bv2) return sortDir2;
    }
    return 0;
  });
}

// ── Header cell ───────────────────────────────────────────────────────────────

const TH_STYLE = {
  padding:       "10px 14px",
  textAlign:     "center",
  fontSize:      T.fsXxs,
  fontWeight:    T.fwBold,
  lineHeight:    "normal",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color:         "var(--v2-mid)",
  background:    "var(--v2-tint)",
  borderBottom:  "1px solid var(--v2-line)",
  userSelect:    "none",
  whiteSpace:    "nowrap",
};

function Th({ col, sortKey, sortDir, onSort, userHasSetSort, v2Presentation }) {
  const centerLabelOnColumn = v2Presentation || col.key === "sqft" || col.key === "psf";
  const isActive = sortKey === col.key && (v2Presentation || userHasSetSort);
  // isSorted reflects the actual data-sort state for aria-sort (accessibility).
  // Stays accurate regardless of whether the user set the sort explicitly.
  const isSorted  = sortKey === col.key;
  const [hovered, setHovered] = useState(false);
  const intent = useHoverIntent();
  const explanationOpen = intent.open;
  const { show: showIntent, dismiss: dismissIntent } = intent;
  const setExplanationOpen = useCallback((value) => value ? showIntent() : dismissIntent(), [showIntent, dismissIntent]);
  const [hoverSuppressed, setHoverSuppressed] = useState(false);
  const pointerFocusRef = useRef(false);
  const pointerInsideRef = useRef(false);
  const hoverDismissedRef = useRef(false);
  const pointerPositionRef = useRef(null);
  const dismissedPositionRef = useRef(null);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const explanation = HEADER_EXPLANATIONS[col.key];
  const showExplanation = Boolean(explanation && explanationOpen);
  const tooltipId = `leases-header-tooltip-${col.key}`;

  const dismissExplanation = useCallback(() => {
    // Retained DOM focus and movement inside the header must not revive a
    // dismissed explanation. Only a new hover entry or focus entry opens it.
    hoverDismissedRef.current = pointerInsideRef.current;
    setHoverSuppressed(pointerInsideRef.current);
    dismissedPositionRef.current = pointerPositionRef.current;
    setExplanationOpen(false);
  }, [setExplanationOpen]);

  const clearHoverSuppression = useCallback(() => {
    hoverDismissedRef.current = false;
    setHoverSuppressed(false);
  }, []);

  useEffect(() => {
    if (!hoverSuppressed) return undefined;
    const rearmOutside = (event) => {
      const rect = headerRef.current?.getBoundingClientRect();
      const dismissedAt = dismissedPositionRef.current;
      if (!rect || !dismissedAt) return;
      if (event.clientX === dismissedAt.x && event.clientY === dismissedAt.y) return;
      if (event.clientX < rect.left || event.clientX >= rect.right
        || event.clientY < rect.top || event.clientY >= rect.bottom) {
        clearHoverSuppression();
      }
    };
    // A layout-driven leave may already put the pointer outside the header;
    // subsequent physical exit movement then has no second header leave.
    window.addEventListener("mousemove", rearmOutside, true);
    return () => window.removeEventListener("mousemove", rearmOutside, true);
  }, [hoverSuppressed, clearHoverSuppression]);

  const trackPointer = (event) => {
    pointerPositionRef.current = { x: event.clientX, y: event.clientY };
  };

  useEffect(() => {
    if (!explanation) return undefined;
    // Capture also catches non-bubbling scrolls from nested List containers.
    window.addEventListener("scroll", dismissExplanation, true);
    return () => window.removeEventListener("scroll", dismissExplanation, true);
  }, [explanation, dismissExplanation]);

  const handleClick   = col.sortable ? () => {
    dismissExplanation();
    onSort(col.key);
  } : undefined;
  const handleKeyDown = col.sortable
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          dismissExplanation();
          onSort(col.key);
        } else if (e.key === "Escape") {
          dismissExplanation();
        }
      }
    : undefined;

  const ariaSort = col.sortable
    ? isSorted ? (sortDir > 0 ? "ascending" : "descending") : "none"
    : undefined;

  const sortChevron = col.sortable && <CollectionSortCaret testId={`v2-sort-indicator-${col.key}`}
    style={centerLabelOnColumn ? { position: "absolute", left: "calc(100% + 3px)", top: "50%", transform: "translateY(-50%)" } : undefined} />;

  return (
    <CollectionSortHeader active={isActive} direction={sortDir} sortable={col.sortable} standard={v2Presentation}
      ref={headerRef}
      className={[col.sortable ? "leases-sort-th" : null, col.colClass ?? null].filter(Boolean).join(" ") || undefined}
      data-testid={`leases-list-th-${col.key}`}
      aria-sort={ariaSort}
      tabIndex={col.sortable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={(event) => { trackPointer(event); pointerFocusRef.current = true; }}
      onMouseDown={(event) => { trackPointer(event); pointerFocusRef.current = true; }}
      onPointerUp={() => { pointerFocusRef.current = false; }}
      onMouseUp={() => { pointerFocusRef.current = false; }}
      onPointerCancel={() => { pointerFocusRef.current = false; }}
      onMouseMove={trackPointer}
      onMouseEnter={() => {
        pointerInsideRef.current = true;
        if (col.sortable) setHovered(true);
      }}
      onMouseLeave={(event) => {
        intent.triggerProps.onMouseLeave(event);
        pointerInsideRef.current = false;
        setHovered(false);
        setExplanationOpen(false);
        // Sorting can move the header under a stationary pointer. That native
        // mouseleave is not a fresh user interaction and must not re-arm hover.
        const dismissedAt = dismissedPositionRef.current;
        if (!dismissedAt || event.clientX !== dismissedAt.x || event.clientY !== dismissedAt.y) {
          clearHoverSuppression();
        }
      }}
      onFocus={explanation ? () => {
        if (!pointerFocusRef.current) {
          clearHoverSuppression();
          setExplanationOpen(true);
        }
      } : undefined}
      onBlur={explanation ? () => {
        pointerFocusRef.current = false;
        dismissExplanation();
      } : undefined}
      aria-describedby={showExplanation ? tooltipId : undefined}
      style={{
        ...TH_STYLE,
        cursor:       col.sortable ? "pointer" : "default",
        width:        col.width ?? undefined,
        maxWidth:     col.width ?? undefined,
        color:        isActive ? "var(--v2-accent)" : "var(--v2-mid)",
        ...(!v2Presentation ? getColHeaderHighlight(col.key, sortKey, userHasSetSort) : {}),
        // For inactive columns: re-assert the hairline AND the tint background (both are
        // wiped to "none" by the inactive getColHeaderHighlight return).
        // For the active column: suppress the grey hairline so only the blue inset
        // boxShadow indicator shows — no dark line stacked beneath it.
        ...(isActive
          ? { borderBottom: "none" }
          : { borderBottom: TH_STYLE.borderBottom, background: TH_STYLE.background }),
      }}
    >
      <div
        data-testid={`leases-list-header-group-${col.key}`}
        style={{
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          alignItems: "center",
          justifyContent: "center",
          ...(centerLabelOnColumn ? { position: "relative" } : {}),
        }}
      >
        <span
          ref={titleRef}
          style={centerLabelOnColumn
            ? { position: "absolute", left: "50%", transform: "translateX(-50%)" }
            : undefined}
          onMouseEnter={explanation ? () => {
            if (!hoverDismissedRef.current) intent.enter();
          } : undefined}
          onMouseLeave={explanation ? () => setExplanationOpen(false) : undefined}
        >
          {col.label}
          {centerLabelOnColumn && sortChevron}
        </span>
        {!centerLabelOnColumn && sortChevron}
      </div>
      {showExplanation && (
        <HeadingTooltip
          triggerRef={titleRef}
          id={tooltipId}
          role="tooltip"
          data-testid={`th-tooltip-${col.key}`}
          style={HEADER_EXPLANATION_STYLE}
        >
          {explanation}
        </HeadingTooltip>
      )}
    </CollectionSortHeader>
  );
}

// ── Urgency heading tooltip ────────────────────────────────────────────────────

const URGENCY_TOOLTIP_TEXT =
  "Urgency guide: Green: Secure (24mo+), Red: Urgent (<12mo)";

const URGENCY_HEADING_TOOLTIP = (
  <ColorLegendPopup rows={[
    { indicator: dotIndicator(T.green,       "green"), label: ACTIVE_LABEL },
    { indicator: dotIndicator(T.amber,       "amber"), label: "Upcoming"   },
    { indicator: dotIndicator(T.red,         "red"),   label: URGENT_LABEL },
    { indicator: dotIndicator(T.defaultDot,  "gray"),  label: "Expired"    },
  ]} />
);

const TH_TIP_STYLE = {
  background:    T.white,
  color:         T.dark,
  border:        `0.5px solid ${T.border}`,
  borderRadius:  8,
  boxShadow:     T.shadowTableHdr,
  padding:       "13px 16px",
  zIndex:        20,
  textTransform: "none",
};

const SR_ONLY = {
  position:   "absolute",
  width:      1,
  height:     1,
  padding:    0,
  margin:     -1,
  overflow:   "hidden",
  clip:       "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border:     0,
};

export function UrgencyTh() {
  const thRef        = useRef(null);
  const intent = useHoverIntent({ interactive: true });
  const tipOpen = intent.open;

  return (
    <th
      ref={thRef}
      data-testid="leases-list-th-urgency"
      tabIndex={0}
      aria-describedby="th-tooltip-urgency"
      aria-haspopup="true"
      aria-expanded={tipOpen}
      {...intent.triggerProps}
      style={{
        ...TH_STYLE,
        cursor: "default",
        width:  90,
      }}
    >
      Urgency
      {!tipOpen && (
        <span id="th-tooltip-urgency" data-testid="th-desc-urgency" style={SR_ONLY}>
          {URGENCY_TOOLTIP_TEXT}
        </span>
      )}
      {tipOpen && (
        <HeadingTooltip
          triggerRef={thRef}
          id="th-tooltip-urgency-popup"
          role="tooltip"
          data-testid="th-tooltip-urgency"
          style={TH_TIP_STYLE}
          {...intent.popupProps}
        >
          {URGENCY_HEADING_TOOLTIP}
        </HeadingTooltip>
      )}
    </th>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

// Defined at module level (not inside LeasesListTable) so React doesn't
// treat it as a new component identity on each render.
function LeaseRow({ lease, matchEvidence, onSelectLocation, onRowClick, getDrawerUrl, compact = false, orderedIds, selected = false, v2Presentation = false }) {
  const navigate = useNavigate();
  const [hover, setHover] = useState(false);
  const [nameHovered, setNameHovered] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);

  const anchor     = resolveTimelineEndAnchor(lease);
  const endD       = anchor.date ? parseDateOnly(anchor.date) : null;
  const isSentinel = endD && endD.getFullYear() > 2100;
  const endLabel   = endD && !isSentinel
    ? (isPastExpiryDate(anchor.date)
      ? formatLongExpiryDate(anchor.date)
      : endD.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }))
    : null;

  const leaseTypeKey = lease.leaseType || lease.type || "";
  const typeLabel = leaseTypeKey
    ? (LEASE_DISPLAY_SHORT[leaseTypeKey] || LEASE_DISPLAY[leaseTypeKey] || leaseTypeKey)
    : null;

  // Expired rows use their last applicable recorded rent for this list only;
  // current-year rent remains the source for KPIs and other aggregates.
  const displayRentUsd = listRentUsd(lease);
  // LEASES-DOUBLE-DOLLAR-FIX: fmtMoney() already returns a "$"-prefixed
  // string (e.g. "$9,349,224 USD"); the template literal must NOT add its
  // own "$" or the cell renders "$$9,349,224 USD".
  const totalCostLabel = displayRentUsd != null
    ? `${fmtMoney(displayRentUsd, "USD")}`
    : null;
  const rentBasis = lease.rentBasis ?? "not_recorded";
  const rentLabel = rentBasis === "rent_free"
    ? "Rent-free"
    : rentBasis === "per_lease_terms"
      ? "Per lease terms"
      : rentBasis === "zero_sublease" && displayRentUsd != null
        ? (
          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.2 }}>
            <span>{fmtMoney(0, "USD")}</span>
            <span style={{ color: "var(--v2-light)", fontSize: T.fsXxs }}>n-a · sublease out</span>
          </span>
        )
        : rentBasis === "fixed" && displayRentUsd != null
          ? totalCostLabel
        : displayRentUsd != null
            ? totalCostLabel
            : "—";

  // Countdown — shown for non-sentinel future dates; past-due marker for past dates.
  const now = new Date();
  let mosRemaining = null;
  let mosPast = null;
  if (endD && !isSentinel) {
    if (endD > now) {
      mosRemaining = Math.round((endD - now) / (1000 * 60 * 60 * 24 * 30.44));
    } else {
      mosPast = Math.round((now - endD) / (1000 * 60 * 60 * 24 * 30.44));
    }
  }
  const countdownStyle = mosRemaining != null ? getCountdownStyle(mosRemaining) : null;

  // Health pill — pre-computed on enriched lease.
  const healthTokens = HEALTH_TIER_STYLE[lease._healthTier] ?? HEALTH_TIER_STYLE.grey;

  // $/sf — pre-computed on enriched lease.
  const psfLabel = lease._psf != null ? `$${Math.round(lease._psf).toLocaleString()}` : null;

  // Next critical date — pre-computed on enriched lease.
  const nextCd = lease._nextCd;
  const nextCdDate  = nextCd?.dueDate ? parseDateOnly(nextCd.dueDate) : null;
  const nextCdLabel = nextCdDate
    ? nextCdDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  const activate = (event) => activateCollectionRow(event,
    getDrawerUrl ? getDrawerUrl(lease.id) : buildLeaseUrl(lease.id),
    () => onRowClick ? onRowClick(lease) : navigate(buildLeaseUrl(lease.id), { state: { orderedIds } }));

  return (
    <tr
      data-testid={`leases-list-row-${lease.id}`}
      className={`leases-collection-row${v2Presentation ? " collection-keyboard-row" : ""}`}
      tabIndex={0}
      onClick={activate}
      onAuxClick={activate}
      onKeyDown={activate}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor:       "pointer",
        background:   selected ? T.rowActive : hover ? T.hoverBg : T.card,
        borderBottom: "1px solid var(--v2-hair)",
        transition:   "background 0.1s",
        height:       v2Presentation ? collectionRowHeight(compact ? "compact" : "comfortable") : matchEvidence ? 42 : compact ? 28 : 38,
      }}
    >
      {/* Office: bold city · lighter alias */}
      <td
        data-collection-identity={v2Presentation ? "" : undefined}
        title={v2Presentation ? undefined : (lease.city || lease.locationId || "—") + (lease.locationAlias ? " · " + lease.locationAlias : "")}
        style={{
          padding:      "0 16px",
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
          maxWidth:     LIST_COL_LEASES_LOCATION_W,
        }}
      >
        <span
          role="button"
          tabIndex={0}
          data-row-name=""
          aria-label={`Open ${lease.city || lease.locationId || "—"}${lease.locationAlias ? " · " + lease.locationAlias : ""}`}
          onMouseEnter={() => setNameHovered(true)}
          onMouseLeave={() => setNameHovered(false)}
          onFocus={() => setNameFocused(true)}
          onBlur={() => setNameFocused(false)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            maxWidth: "100%",
            overflow: "hidden",
            cursor: "pointer",
            borderRadius: 6,
            padding: "3px 7px",
            margin: "-3px -7px",
            background: (nameHovered || nameFocused) ? "var(--v2-tint)" : "transparent",
            outline: "none",
            transition: "background .12s",
          }}
        >
          <span data-collection-name={v2Presentation ? "" : undefined} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <span style={{ fontWeight: T.fwSemibold, fontSize: T.fsBase, color: "var(--v2-dark)" }}>
              {lease.city || lease.locationId || "—"}
            </span>
            {lease.locationAlias && (
              <span style={{ color: "var(--v2-light)", fontWeight: T.fwRegular, fontSize: T.fsXs }}>
                {" · "}{lease.locationAlias}
              </span>
            )}
          </span>
          <span
            aria-hidden="true"
            style={{
              flexShrink: 0,
              color: "var(--v2-light)",
              fontSize: T.fsSm,
              opacity: (nameHovered || nameFocused) ? 1 : 0,
              transform: (nameHovered || nameFocused) ? "translateX(0)" : "translateX(-4px)",
              transition: "opacity .12s, transform .12s",
            }}
          >
            ↗
          </span>
        </span>
        <IdentityMatchHint value={matchEvidence} inline={v2Presentation} />
      </td>

      {/* Hub — col 2, mirrors Hub column in Offices list */}
      <td data-testid={`leases-hub-col-${lease.id}`} style={{ padding: "0 16px", textAlign: "center" }}>
        {lease.isHub
          ? <span title="Hub" style={{ color: T.hubChipText, fontSize: T.fsSm, lineHeight: 1 }}>◆</span>
          : <span style={{ color: "var(--v2-light)" }}>—</span>
        }
      </td>

      {/* Health pill — col 3, RAG word label matching Timeline band */}
      {/* LEASES-LIST-HEALTH-POLISH: constructed identically to StatusCell (S.badge +
          border: 1px solid T.border) so pills are pixel-matched across both columns. */}
      <td style={{ padding: "0 16px", textAlign: "center" }}>
        {lease._healthWord ? (
          <span
            data-testid="health-pill"
            style={{
              display:        "inline-flex",
              alignItems:     "center",
              justifyContent: "center",
              borderRadius:   6,
              height:         22,
              lineHeight:     1,
              padding:        "0 10px",
              fontSize:       T.fsXs,
              fontWeight:     T.fwSemibold,
              whiteSpace:     "nowrap",
              minWidth:       OUTLOOK_PILL_MIN_W,
              textAlign:      "center",
              color:          healthTokens.color,
              background:     healthTokens.background,
            }}
          >
            {lease._healthWord}
          </span>
        ) : (
          <span style={{ color: "var(--v2-light)" }}>—</span>
        )}
      </td>

      {/* Status */}
      <td style={{ padding: "0 16px", textAlign: "center", verticalAlign: "middle" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <StatusCell lease={lease} />
        </div>
      </td>

      {/* Expire Date + countdown dot text */}
      {/* LEASES-LIST-EXPIRE-BLEED-v2: explicit maxWidth (column width minus
          padding/gutter) + overflow guard so the longest realistic string
          ("Sep 30, 2024 · 89mo ago") clips within this cell instead of
          bleeding into the Type column to the right.
          LEASES-LIST-HEALTH-POLISH: moved to immediately after Status.
          LEASES-LIST-EXPIRE-COLUMN-WIDTH: 210px confirmed to fit the longest
          known expired string ("Sep 30, 2024 · 89mo ago") with 8px padding. */}
      <td title={v2Presentation && endLabel ? `${endLabel}${mosPast != null ? ` · ${mosPast < 1 ? "past due" : `${mosPast}mo ago`}` : mosRemaining != null ? ` · ${mosRemaining}mo` : ""}` : undefined} style={{ padding: "0 8px", textAlign: "left", whiteSpace: "nowrap", maxWidth: LIST_COL_LEASES_EXPIRE_DATE_W, overflow: "hidden", textOverflow: "ellipsis" }}>
        {endLabel ? (
          <span style={{ display: "inline-flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
            <span
              data-testid="expire-date-value"
              style={{
                color: "var(--v2-mid)",
                fontSize: T.fsBase,
                textAlign: "right",
                minWidth: "90px",
                display: "inline-block",
              }}
            >
              {endLabel}
            </span>
            {mosRemaining != null && countdownStyle != null && (
              <>
                <span aria-hidden="true" style={{ color: T.borderDark, fontSize: T.fsBase, margin: "0 5px" }}>·</span>
                <span
                  data-testid="expire-countdown"
                  style={{ color: countdownStyle.color, fontSize: T.fsBase, fontWeight: countdownStyle.fontWeight, textAlign: "left", minWidth: "36px", display: "inline-block" }}
                >
                  {mosRemaining}mo
                </span>
              </>
            )}
            {mosPast != null && (
              <>
                <span aria-hidden="true" style={{ color: T.borderDark, fontSize: T.fsBase, margin: "0 5px" }}>·</span>
                <span
                  data-testid="expire-countdown"
                  style={{ color: T.red, fontSize: T.fsBase, fontWeight: T.fwSemibold, textAlign: "left", minWidth: "36px", display: "inline-block" }}
                >
                  {mosPast < 1 ? "past due" : `${mosPast}mo ago`}
                </span>
              </>
            )}
          </span>
        ) : (
          <span style={{ color: "var(--v2-light)" }}>—</span>
        )}
      </td>

      {/* Type — LEASES-LIST-HEALTH-POLISH: moved to after Expire Date */}
      <td className="leases-col-type" style={{ padding: "0 16px", textAlign: "center" }}>
        {typeLabel ? (
          <span
            data-testid="lease-type-stack"
            style={{ display: "flex", width: "100%", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, lineHeight: 1.15, textAlign: "center" }}
          >
            <span
              data-testid="lease-type-value"
              style={{ display: "block", width: "100%", color: "var(--v2-mid)", fontSize: T.fsBase, whiteSpace: "nowrap", textAlign: "center" }}
            >
              {typeLabel}
            </span>
            {!(v2Presentation && compact) && (lease.headLeaseId ? (
              <span
                data-testid={`sublease-head-relation-${lease.id}`}
                title={v2Presentation ? `of ${lease.headLeaseId}` : undefined}
                style={LEASE_RELATIONSHIP_TEXT_STYLE}
              >
                of {lease.headLeaseId}
              </span>
            ) : lease.isSubleaseHead ? (
              <span
                data-testid={`sublease-head-label-${lease.id}`}
                title={v2Presentation ? `Head · ${lease.subleaseChildCount} sub-outs` : undefined}
                style={LEASE_RELATIONSHIP_TEXT_STYLE}
              >
                Head · {lease.subleaseChildCount} sub-outs
              </span>
            ) : null)}
          </span>
        ) : (
          <span style={{ color: "var(--v2-light)" }}>—</span>
        )}
      </td>

      <td className="leases-col-term" title={v2Presentation ? lease.termType || undefined : undefined} data-testid={`lease-term-type-${lease.id}`} style={{ padding: "0 16px", fontSize: T.fsBase, whiteSpace: "nowrap", textAlign: "center", ...(v2Presentation ? { color: "var(--v2-mid)", fontWeight: T.fwRegular } : {}) }}>
        {lease.termType ?? ""}
      </td>

      {/* Sq Ft — LEASES-LIST-HEALTH-POLISH: moved to after Type */}
      <td className="leases-col-sqft" style={{ padding: "0 16px", color: "var(--v2-mid)", fontSize: T.fsBase, whiteSpace: "nowrap", textAlign: "right" }}>
        {currentLeaseFootprint(lease).grossSf === 0 ? "0" : fmtSqft(currentLeaseFootprint(lease).grossSf) || <span style={{ color: "var(--v2-light)" }}>—</span>}
      </td>

      {/* Annual Rent (totalCost) */}
      <td data-testid="annual-rent-cell" className="leases-col-rent" style={{ padding: "0 16px", color: "var(--v2-mid)", fontSize: T.fsBase, whiteSpace: "nowrap", textAlign: "right" }}>
        {rentLabel}
        {!v2Presentation && <AnnualRentBreakdown lease={lease} />}
      </td>

      {/* $/SF */}
      <td data-testid="psf-cell" className="leases-col-psf" style={{ padding: "0 16px", color: "var(--v2-mid)", fontSize: T.fsBase, whiteSpace: "nowrap", textAlign: "right" }}>
        {psfLabel || <span style={{ color: "var(--v2-light)" }}>—</span>}
      </td>

      {/* A flexible spare-space gutter, not an action reservation. */}
      <td className="leases-spare" aria-hidden="true" style={{ padding: 0 }} />
    </tr>
  );
}

// ── Top-level component ───────────────────────────────────────────────────────
//
// Props:
//   leases             — pre-filtered array; pre-sorted when sortMode="dropdown"
//   totalLeaseCount    — unfiltered lease count; when 0 shows "no leases yet"
//   cdByLease          — { [leaseId]: CriticalDateRow[] } for Next CD column
//   onSelectLocation   — (locationId, tab?) => void
//   leaseSortKey       — currently active column key, or null
//   leaseSortDir       — 1 (asc) | -1 (desc)
//   leaseSortKey2      — optional "Then by" tie-break key, or null (LIST-SORT-ROLLOUT-S1)
//   leaseSortDir2      — 1 (asc) | -1 (desc), only used when leaseSortKey2 is set
//   onLeaseSort        — (key, dir) => void
//   onClearFilters     — () => void | undefined

export function LeasesListTable({
  leases = [],
  searchQuery = "",
  totalLeaseCount,
  loading = false,
  onSelectLocation,
  onEditLease,
  onRowClick,
  getDrawerUrl,
  cdByLease,
  leaseSortKey = null,
  leaseSortDir = 1,
  leaseSortKey2 = null,
  leaseSortDir2 = 1,
  onLeaseSort,
  onClearFilters,
  emptyStateExplanation = null,
  density = "comfortable",
  userHasSetSort = false,
  presorted = false,
  orderedIds,
  selectedLeaseId = null,
  onSortedIdsChange = () => {},
  // Opt-in at the active v2 page boundary; retained callers keep their defaults.
  presentation = "legacy",
}) {
  const v2Presentation = presentation === "v2";
  const isTrulyEmpty = (totalLeaseCount ?? leases.length) === 0;

  const shellRef = useRef(null);
  useCollectionCellTitles(shellRef, '[data-testid^="leases-list-row-"] > td', v2Presentation);
  const headerScrollRef = useRef(null);
  const bodyScrollRef   = useRef(null);
  const sentinelRef = useRef(null);
  const isStuck = useListHeaderStuck({ sentinelRef, headerScrollRef, enabled: leases.length > 0 });
  const [identityVisibility, setIdentityVisibility] = useState({ arrangementVisible: true, termVisible: true });
  // The existing responsive CSS can hide these columns. Evidence must explain
  // those matches too, rather than assuming the desktop columns are visible.
  React.useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;
    const measure = () => {
      const visible = (key) => {
        const header = shell.querySelector(`[data-testid="leases-list-th-${key}"]`);
        return !header || getComputedStyle(header).display !== "none";
      };
      const arrangementVisible = visible("type");
      const termVisible = visible("term_type");
      setIdentityVisibility((prev) => prev.arrangementVisible === arrangementVisible && prev.termVisible === termVisible
        ? prev : { arrangementVisible, termVisible });
    };
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(shell);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [v2Presentation, loading]);

  const handleBodyScroll = useCallback(() => {
    if (headerScrollRef.current && bodyScrollRef.current) {
      headerScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    }
  }, []);

  const handleSort = useCallback((key) => {
    if (!onLeaseSort) return;
    if (leaseSortKey === key) {
      onLeaseSort(key, -leaseSortDir);
    } else {
      const col = COLUMNS.find((c) => c.key === key);
      onLeaseSort(key, col?.defaultDir ?? 1);
    }
  }, [leaseSortKey, leaseSortDir, onLeaseSort]);

  // Enrich each lease with derived fields for all new columns so that the
  // sort function can access them as plain fields on the enriched object.
  const subleaseChildCountByHead = useMemo(() => {
    const counts = new Map();
    for (const lease of leases) {
      if (!lease.headLeaseId) continue;
      counts.set(lease.headLeaseId, (counts.get(lease.headLeaseId) || 0) + 1);
    }
    return counts;
  }, [leases]);

  const enriched = useMemo(() => {
    const allCds = Object.values(cdByLease || {}).flat();
    return leases.map((lease) => {
      const leaseRisk       = computeLeaseRisk(lease);
      const seededRiskScore = leaseRisk.score;
      const seededRiskBand  = leaseRisk.band;
      const leaseCds = (cdByLease?.[lease.id] || [])
        .filter((cd) => cd.daysUntil != null && cd.daysUntil >= 0)
        .sort((a, b) => a.daysUntil - b.daysUntil);
      const nextCd   = leaseCds[0] ?? null;
      const listRentUsd = lease.listDisplayRentUsd !== undefined
        ? lease.listDisplayRentUsd
        : lease.annualRentUsd;
      const psf = displayedLeasePsf(lease);
      const renewalCount = Number(lease.renewalCount) || 0;
      const renewalTerm  = Number(lease.renewalTermYears) || 0;
      const renewalOption = renewalCount > 0 && renewalTerm > 0
        ? `${renewalCount}×${renewalTerm}yr`
        : renewalCount > 0
          ? `${renewalCount} option(s)`
          : null;
      const health = classifyLeaseHealth(lease, allCds);
      return {
        ...lease,
        isSubleaseHead: !!lease.isSubleaseHead || (subleaseChildCountByHead.get(lease.id) || 0) > 0,
        subleaseChildCount: Math.max(
          Number(lease.subleaseChildCount) || 0,
          subleaseChildCountByHead.get(lease.id) || 0,
        ),
        _listRentUsd:   listRentUsd,
        _riskScore:      seededRiskScore,
        _riskBand:       seededRiskBand,
        _nextCd:         nextCd,
        _psf:            psf,
        _renewalOption:  renewalOption,
        _healthBucket:   health.bucket,
        _healthWord:     health.word,
        _healthTier:     health.colorTier,
      };
    });
  }, [leases, cdByLease, subleaseChildCountByHead]);

  // Memoize sorted rows so the reference is stable when sort inputs are unchanged.
  // Without memoization, applySortInternal returns a new array on every render
  // (even if nothing changed), causing the onSortedIdsChange effect to fire
  // every render → setListSortedIds in parent → re-render → repeat (render loop).
  const searchMatches = useMemo(() => searchQuery.trim()
    ? new Map(enriched.map((lease) => [lease.id, matchLeaseIdentity(lease, searchQuery)]))
    : new Map(), [enriched, searchQuery]);
  const rows = useMemo(
    // The v2 collection parent owns canonical sorting and search ranking, also
    // used by drawer navigation. Retained readers keep their internal sorter.
    () => presorted ? enriched : rankIdentityMatches(applySortInternal(enriched, leaseSortKey, leaseSortDir, leaseSortKey2, leaseSortDir2), searchMatches),
    [enriched, leaseSortKey, leaseSortDir, leaseSortKey2, leaseSortDir2, searchMatches, presorted],
  );
  const navigationIds = useMemo(() => searchQuery.trim() ? rows.map((row) => row.id) : orderedIds,
    [rows, searchQuery, orderedIds]);
  const searchEvidence = useMemo(() => searchQuery.trim()
    ? new Map(rows.map((lease) => [lease.id, matchLeaseIdentity(lease, searchQuery, identityVisibility).evidence]))
    : new Map(), [rows, searchQuery, identityVisibility]);

  // LIST-KEYBOARD-NAV-ROW-HIGHLIGHT (G-25): Report the sorted row order to the
  // parent so arrow-key nav steps through rows in the exact visual order the
  // table renders, not the unsorted prop order. Mirrors onOrderedIdsChange in
  // LeasesTimelineView. Default prop is a no-op so existing call sites are safe.
  // onSortedIdsChange is setListSortedIds from useState — stable across renders.
  useEffect(() => {
    onSortedIdsChange(rows.map((r) => r.id));
  }, [rows, onSortedIdsChange]);

  const totalCount = totalLeaseCount ?? leases.length;

  const geometry = useLeasesListGeometry({
    enabled: v2Presentation, shellRef, headerRef: headerScrollRef,
    bodyRef: bodyScrollRef, rows, density,
  });
  const columns = v2Presentation
    ? COLUMNS.map((col, index) => ({ ...col, width: geometry.widths[index] }))
    : COLUMNS;

  const COLGROUP = (
    <colgroup>
      {/* Every data column gets an explicit width so both header and body tables
          distribute columns identically and headers stay directly over their data.
          LEASES-LIST-HEALTH-POLISH column order:
          Office · Hub · Health · Status · Expire Date · Arrangement · Term Type · Sq Ft · Annual Rent · $/SF */}
      {v2Presentation ? <>
        {columns.map((col) => (
          <col key={col.key} className={col.key === "location" ? "leases-col-office" : col.colClass} style={{ width: col.width }} />
        ))}
        <col style={{ width: geometry.widths[10] }} />
      </> : <>
      <col style={{ width: LIST_COL_LEASES_LOCATION_W }} />
      <col style={{ width: LIST_COL_LEASES_HUB_W }} />
      <col style={{ width: LIST_COL_LEASES_HEALTH_W }} />
      <col style={{ width: LIST_COL_LEASES_STATUS_W }} />
      <col style={{ width: LIST_COL_LEASES_EXPIRE_DATE_W }} />
      <col style={{ width: LIST_COL_LEASES_TRAILING_W }} />
      <col className="leases-col-term" style={{ width: LIST_COL_LEASES_TRAILING_W }} />
      <col style={{ width: LIST_COL_LEASES_TRAILING_W }} />
      <col style={{ width: LIST_COL_LEASES_ANNUAL_RENT_W }} />
      <col style={{ width: LIST_COL_LEASES_TRAILING_W }} />
      {/* Spacer col — no explicit width; absorbs all remaining table width */}
      <col />
      </>}
    </colgroup>
  );

  return (
    <div ref={shellRef} data-testid="leases-list-table-shell" data-density={density} className={v2Presentation ? "leases-list-v2" : "leases-list-legacy"} style={v2Presentation ? { containerType: "inline-size", containerName: "leases-list", minWidth: 0 } : undefined}>
      <div
        data-testid="leases-list-table-scroll"
        style={{
          border:       `1px solid ${T.border}`,
          borderRadius: PANEL_RADIUS,
          boxShadow:    T.cardShadow,
          background:   T.card,
        }}
      >
        <style>{LEASES_TH_CSS}</style>

        {/* Sticky shell — pins the header row at the top of the page while
            rows scroll beneath. No overflow so position:sticky works against
            the page scroll container (not this div). */}
        <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} />
        <div data-testid="leases-list-header-sticky" style={{ position: "sticky", top: 0, zIndex: 10 }}>
          {/* Clip div — hides the horizontal overflow so the header doesn't
              show a scrollbar; scrollLeft is driven by bodyScrollRef via
              handleBodyScroll so columns stay aligned during horizontal scroll.
              The top-corner radius here is NOT redundant with the card's radius —
              the card has no overflow:hidden, so without these the header
              background escapes the card's rounded top corners. */}
          {/* background: "var(--v2-tint)" fills the top-left corner cell area so
              the card's white T.card background doesn't bleed through the rounded
              corners on scroll, producing the visible white-square artefact. */}
          <div data-testid="leases-list-header-clip" ref={headerScrollRef} style={{ overflowX: "hidden", ...listHeaderEdges(isStuck), background: "var(--v2-tint)" }}>
            <OverflowTable overflowScrollRef={bodyScrollRef}
              data-testid="leases-list-header-table"
              style={{
                width:          v2Presentation ? geometry.tableWidth : "100%",
                minWidth:       v2Presentation ? 0 : 1111,
                tableLayout:    "fixed",
                borderCollapse: "collapse",
                fontSize:       T.fsSm5,
              }}
            >
              {COLGROUP}
              <thead>
                <tr>
                  {columns.map((col) => (
                    <Th
                      key={col.key}
                      col={col}
                      sortKey={leaseSortKey}
                      sortDir={leaseSortDir}
                      onSort={handleSort}
                      userHasSetSort={userHasSetSort}
                      v2Presentation={v2Presentation}
                    />
                  ))}
                  {/* Spacer header — matches spacer col */}
                  <th aria-hidden="true" style={{ padding: 0, border: "none", background: "var(--v2-tint)", borderBottom: "1px solid var(--v2-hair)" }} />
                </tr>
              </thead>
            </OverflowTable>
          </div>
        </div>

        {/* Body wrapper — horizontal scroll; onScroll keeps header aligned */}
        <div ref={bodyScrollRef} onScroll={handleBodyScroll} style={{ overflowX: "auto" }}>
          <OverflowTable
            data-testid="leases-list-table"
            style={{
              width:          v2Presentation ? geometry.tableWidth : "100%",
              minWidth:       v2Presentation ? 0 : 1111,
              tableLayout:    "fixed",
              borderCollapse: "collapse",
              fontSize:       T.fsSm5,
            }}
          >
            {COLGROUP}
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    data-testid="leases-list-loading"
                    style={{ padding: "48px 12px", textAlign: "center", color: "var(--v2-light)", fontSize: T.fsSm }}
                  >
                    Loading leases…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    data-testid="leases-list-empty"
                    style={{ padding: "32px 12px", textAlign: "center", color: T.mid, fontSize: T.fsBase }}
                  >
                    {emptyStateExplanation ?? (isTrulyEmpty ? LEASES_TRULY_EMPTY_MESSAGE : LEASES_EMPTY_STATE_MESSAGE)}
                    {!emptyStateExplanation && onClearFilters && (
                      <button
                        type="button"
                        data-testid="leases-list-empty-clear-filters"
                        onClick={onClearFilters}
                        style={{
                          background: "transparent",
                          border:     "none",
                          padding:    0,
                          marginTop:  6,
                          fontSize:   T.fsBase,
                          color:      T.blue,
                          cursor:     "pointer",
                          display:    "block",
                          margin:     "6px auto 0",
                        }}
                      >
                        Clear filters
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((lease) => (
                  <LeaseRow
                    matchEvidence={searchEvidence.get(lease.id)}
                    key={lease.id}
                    lease={lease}
                    onSelectLocation={onSelectLocation}
                    onRowClick={onRowClick}
                    getDrawerUrl={getDrawerUrl}
                    compact={density === "compact"}
                    v2Presentation={v2Presentation}
                    orderedIds={navigationIds}
                    selected={selectedLeaseId === lease.id}
                  />
                ))
              )}
            </tbody>
          </OverflowTable>
        </div>
      </div>
    </div>
  );
}
